import { useState, useEffect } from 'react';
import { format, addDays, addYears, isBefore, parseISO } from 'date-fns';
import { useSchedulePulsePollingMutation, useGetPulsePollingConfigQuery, useUpdatePulsePollingConfigMutation, useDeletePulsePollingConfigMutation } from '../../redux/survey/survey.endpoints';
import { Check } from 'lucide-react';

interface PulsePollingProps {
  extSurveyId: string;
}

const PulsePolling = ({ extSurveyId }: PulsePollingProps) => {
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const oneYearFromTomorrow = format(addYears(new Date(), 1), 'yyyy-MM-dd');

  const [frequency, setFrequency] = useState<'day' | 'week' | 'month'>('day');
  const [panelSize, setPanelSize] = useState<number>(100);
  const [startDate, setStartDate] = useState<string>(tomorrow);
  const [endDate, setEndDate] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [schedulePulsePolling] = useSchedulePulsePollingMutation();
  const [updatePulsePolling] = useUpdatePulsePollingConfigMutation();
  const [deletePulsePolling] = useDeletePulsePollingConfigMutation();
  const { data: existingConfig, isLoading } = useGetPulsePollingConfigQuery({ ext_survey_id: extSurveyId }, {
    skip: !extSurveyId,
    // Treat errors as no config existing
    selectFromResult: ({ data, isLoading, error }) => ({
      data: error ? null : data,
      isLoading,
    }),
  });

  const handlePanelSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (value >= 1 && value <= 1000) {
      setPanelSize(value);
      setErrors(prev => ({ ...prev, panelSize: '' }));
    } else {
      setErrors(prev => ({ ...prev, panelSize: 'Panel size must be between 1 and 1000' }));
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    if (endDate && isBefore(parseISO(endDate), parseISO(newStartDate))) {
      setEndDate('');
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
  };

  // Set default end date to one week from start date (when start date is set)
  useEffect(() => {
    if (startDate && !endDate) {
      const oneWeekFromStartDate = format(addDays(parseISO(startDate), 7), 'yyyy-MM-dd');
      setEndDate(oneWeekFromStartDate);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (existingConfig) {
      setFrequency(existingConfig.interval);
      setPanelSize(existingConfig.completes);

      const formatDateForInput = (dateString: string) => {
        try {
          const date = new Date(dateString);
          return format(date, 'yyyy-MM-dd');
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      };

      setStartDate(formatDateForInput(existingConfig.start_date));
      setEndDate(formatDateForInput(existingConfig.end_date));
    }
  }, [existingConfig]);

  const handleSubmit = async() => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (existingConfig && isEditing) {
        await updatePulsePolling({
          id: existingConfig.id,
          interval: frequency,
          completes: panelSize,
          start_date: startDate,
          end_date: endDate,
        }).unwrap();
        setIsEditing(false);
        setErrors({});
      } else {
        await schedulePulsePolling({
          interval: frequency,
          completes: panelSize,
          start_date: startDate,
          end_date: endDate,
          survey: extSurveyId,
        }).unwrap();
        setErrors({});
      }
    } catch (error) {
      console.error('Error scheduling pulse polling:', error);
      setSubmitError('An error occurred while scheduling pulse polling. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    if (existingConfig) {
      setFrequency(existingConfig.interval);
      setPanelSize(existingConfig.completes);

      const formatDateForInput = (dateString: string) => {
        try {
          const date = new Date(dateString);
          return format(date, 'yyyy-MM-dd');
        } catch (error) {
          console.error('Error formatting date:', error);
          return '';
        }
      };

      setStartDate(formatDateForInput(existingConfig.start_date));
      setEndDate(formatDateForInput(existingConfig.end_date));
    }
  };

  const handleDelete = async() => {
    if (!existingConfig) return;
    try {
      await deletePulsePolling({ id: existingConfig.id, survey: extSurveyId }).unwrap();
      setFrequency('day');
      setPanelSize(100);
      setStartDate(tomorrow);
      setEndDate('');
      setErrors({});
      setIsEditing(false);
    } catch (error) {
      console.error('Error deleting pulse polling config:', error);
      setSubmitError('An error occurred while deleting the pulse polling configuration. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4">
        Loading pulse polling schedule...
      </div>
    );
  }

  const showForm = !existingConfig || isEditing;

  return (
    <div className="space-y-4">
      <div className="font-semibold mr-2 flex items-center">
        Auto-recurring Pulse Polling
        {existingConfig && (
          <span className="inline-flex items-center justify-center rounded-full py-1 px-2 ml-1 text-xs font-bold bg-white text-blue-500">
            <span className="size-1 rounded-full inline-block mr-1 bg-blue-500" />
            {existingConfig.status}
          </span>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Frequency
            </label>
            <select
              className={`w-full h-12 p-2 border border-gray-300 rounded-xl ${!showForm ? 'opacity-100 text-gray-900' : ''}`}
              style={!showForm ? { background: '#fafafa' } : undefined}
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as 'day' | 'week' | 'month')}
              disabled={!showForm}
            >
              <option value="day">
                Daily
              </option>
              <option value="week">
                Weekly
              </option>
              <option value="month">
                Monthly
              </option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              AI Panel Size
            </label>
            <input
              type="number"
              min="1"
              max="1000"
              value={panelSize}
              onChange={handlePanelSizeChange}
              className="w-full h-12 p-2 border border-gray-300 rounded-xl"
              disabled={!showForm}
            />
            {errors.panelSize && (
              <p className="text-red-500 text-xs">
                {errors.panelSize}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              min={tomorrow}
              max={oneYearFromTomorrow}
              value={startDate}
              onChange={handleStartDateChange}
              className="w-full h-12 p-2 border border-gray-300 rounded-xl"
              disabled={!showForm}
            />
            {errors.startDate && (
              <p className="text-red-500 text-xs">
                {errors.startDate}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              min={startDate ? format(addDays(parseISO(startDate), 1), 'yyyy-MM-dd') : tomorrow}
              max={oneYearFromTomorrow}
              value={endDate}
              onChange={handleEndDateChange}
              className="w-full h-12 p-2 border border-gray-300 rounded-xl"
              disabled={!startDate || !showForm}
            />
            {errors.endDate && (
              <p className="text-red-500 text-xs">
                {errors.endDate}
              </p>
            )}
          </div>
        </div>

        {submitError && (
          <div className="mt-2 text-red-500 text-sm">
            {submitError}
          </div>
        )}

        <div className="flex space-x-4 mt-6">
          {showForm ? (
            <>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !startDate || !endDate}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : (
                  isEditing ? 'Save' : (
                    <div className="w-full flex items-center justify-center gap-1.5 text-sm md:text-base font-medium mx-auto">
                      <Check
                        width="12px"
                        height="12px"
                        stroke="#fff"
                      />
                      Schedule
                    </div>
                  )
                )}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-4 rounded-xl"
                >
                  Cancel
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleEdit}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-xl"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-xl"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PulsePolling;